import { gql } from '../../core/graphql-client.js';
import type { Schedule, ScheduleAssignment, Saturday } from '../../types.js';

export class PublishedSchedulePage extends HTMLElement {
  connectedCallback(): void {
    this.innerHTML = '<loading-spinner text="Ładowanie grafiku..."></loading-spinner>';
    this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      // Get active quarter
      const qData = await gql.query<{ quarters: Array<{ year: number; quarterNum: number; label: string; isActive: boolean }> }>(`
        query { quarters { year quarterNum label isActive } }
      `);
      const quarter = qData.quarters.find(q => q.isActive);
      if (!quarter) {
        this.innerHTML = '<div class="page"><p>Brak aktywnego kwartału.</p></div>';
        return;
      }

      // Get published schedule
      const sData = await gql.query<{ schedule: Schedule | null }>(`
        query($year: Int!, $qn: Int!) {
          schedule(year: $year, quarterNum: $qn, status: PUBLISHED) {
            quarterYear quarterNum status publishedAt
            assignments {
              saturdayDate roleSlug
              saturday { date eventNote isSkipped }
              role { slug displayName sortOrder }
              person { id displayName }
              isLocked note
            }
          }
        }
      `, { year: quarter.year, qn: quarter.quarterNum });

      if (!sData.schedule) {
        this.innerHTML = '<div class="page"><p>Grafik nie został jeszcze opublikowany.</p></div>';
        return;
      }

      this.renderSchedule(quarter.label, sData.schedule);
    } catch (e) {
      this.innerHTML = `<div class="page"><p style="color:var(--color-error);">Błąd: ${e instanceof Error ? e.message : e}</p></div>`;
    }
  }

  private renderSchedule(quarterLabel: string, schedule: Schedule): void {
    // Group assignments by saturday
    const byDate = new Map<string, ScheduleAssignment[]>();
    for (const a of schedule.assignments) {
      if (a.saturday.isSkipped) continue;
      const key = a.saturday.date;
      if (!byDate.has(key)) byDate.set(key, []);
      byDate.get(key)!.push(a);
    }

    // Sort each group by role sortOrder
    for (const group of byDate.values()) {
      group.sort((a, b) => a.role.sortOrder - b.role.sortOrder);
    }

    const formatDate = (iso: string) => {
      const d = new Date(iso);
      return d.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' });
    };

    // Sort dates
    const dates = [...byDate.keys()].sort();

    this.innerHTML = `
      <div class="page" style="padding-bottom:80px;">
        <div class="page-header">
          <h1>Grafik — ${quarterLabel}</h1>
        </div>
        ${dates.map(date => {
          const assignments = byDate.get(date)!;
          const saturday = assignments[0].saturday;
          return `
            <div class="card" style="margin-bottom:16px;">
              <h3 style="margin-bottom:8px;">${formatDate(date)}</h3>
              ${saturday.eventNote ? `<p style="font-size:.8rem;color:var(--color-on-surface-muted);margin-bottom:8px;">${saturday.eventNote}</p>` : ''}
              <div style="display:flex;flex-direction:column;gap:4px;">
                ${assignments.map(a => `
                  <div style="display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid var(--color-surface-container);">
                    <span style="font-size:.875rem;color:var(--color-on-surface-muted);">${a.role.displayName}</span>
                    <span style="font-size:.875rem;font-weight:500;${!a.person ? 'color:var(--color-error);' : ''}">${a.person?.displayName ?? '—'}</span>
                  </div>
                `).join('')}
              </div>
            </div>
          `;
        }).join('')}
      </div>
    `;
  }
}

customElements.define('published-schedule-page', PublishedSchedulePage);
